/**
 * Every fleet_operator account with real transaction history under the Philadelphia Bus Terminal
 * payee (id 64b8dc3b-7131-461d-8cd2-331c9b1ff698), verified against prod for AUT-5627 by querying
 * financial.transaction directly (not the financial_transaction_log_no_balance view, which joins
 * in an expensive per-row permit-type lookup and times out on this query shape). This list was
 * originally built by filtering account.organization_name for "%bus%", which silently missed
 * every carrier whose name doesn't contain that literal string (Krapf's Coaches, Cavalier Coach,
 * Adventure Tours, Fullington, and others below) -- caught in review (PR comment from mrxinu) and
 * corrected by re-deriving the list directly from transaction volume instead of a name pattern.
 * Excludes owner_type='individual_driver' accounts, and three owner_type='fleet_operator'
 * accounts that are unambiguously not bus carriers: a taxi company and a food-service company
 * (one incidental transaction each), and one unnamed account (organization_name is blank) with
 * two transactions, both on the same day (2026-02-26). None of the three operates buses at the
 * terminal.
 *
 * Gates whether the Disputed Transactions tab is visible at all. Filtered to drop any unset env
 * var rather than letting `undefined` sit in the array, since an unset var must never
 * accidentally satisfy an `includes()` check.
 */
export const PHILLY_BUS_CARRIER_ACCOUNT_IDS: string[] = [
  process.env.REACT_APP_GREYHOUND_BUS_ACCOUNT_ID,
  process.env.REACT_APP_PETER_PAN_BUS_ACCOUNT_ID,
  process.env.REACT_APP_ADVENTURE_TOURS_ACCOUNT_ID,
  process.env.REACT_APP_CAVALIER_COACH_ACCOUNT_ID,
  process.env.REACT_APP_KRAPFS_COACHES_ACCOUNT_ID,
  process.env.REACT_APP_EXPRESSBUS_ACCOUNT_ID,
  process.env.REACT_APP_FULLINGTON_ACCOUNT_ID,
  process.env.REACT_APP_A_ACE_BUS_CHARTER_ACCOUNT_ID,
  process.env.REACT_APP_SUPER_BUS_ACCOUNT_ID,
  process.env.REACT_APP_OWLBUS_FLEET_ACCOUNT_ID,
  process.env.REACT_APP_BETHANY_LINE_TOURS_ACCOUNT_ID,
  process.env.REACT_APP_IVY_TOURS_ACCOUNT_ID,
  process.env.REACT_APP_TRIBAL_SUN_ACCOUNT_ID,
  process.env.REACT_APP_AMERICAN_DREAM_COACHES_ACCOUNT_ID,
  process.env.REACT_APP_CHENG_YA_ACCOUNT_ID,
  process.env.REACT_APP_EAGLE_BUS_ACCOUNT_ID,
  process.env.REACT_APP_ACE_TRAVEL_ACCOUNT_ID,
  process.env.REACT_APP_FRANK_MARTZ_COACH_ACCOUNT_ID,
  process.env.REACT_APP_GUNTHER_CHARTERS_ACCOUNT_ID,
  process.env.REACT_APP_OURBUS_ACCOUNT_ID,
].filter((id): id is string => Boolean(id));

/**
 * Of the Philly bus carriers in PHILLY_BUS_CARRIER_ACCOUNT_IDS, only Peter Pan Bus's account is
 * the one the 4 hardcoded transactions in disputedTransactions.ts actually belong to. Every other
 * Philly bus carrier sees the tab (per PHILLY_BUS_CARRIER_ACCOUNT_IDS) but an empty table, since
 * those rows aren't theirs.
 *
 * Deliberately kept in this file, separate from disputedTransactions.ts: this file is a normal,
 * always-loaded static import (PayerDashboard needs it synchronously to decide props), while
 * disputedTransactions.ts holds the actual transaction data and is dynamically imported --
 * fetched as its own chunk only when isDisputedTransactionsOwner is true -- so every other
 * carrier's browser never requests it at all. Splitting on "which file has the real data" rather
 * than "which file has account ids" is what makes that split meaningful.
 */
export const DISPUTED_TRANSACTIONS_ACCOUNT_ID = process.env.REACT_APP_PETER_PAN_BUS_ACCOUNT_ID;

/**
 * True only when `accountId` matches the confirmed owner AND that owner is actually configured.
 * The `Boolean(...)` guard matters: without it, an unset `DISPUTED_TRANSACTIONS_ACCOUNT_ID` would
 * make `undefined === undefined` look like a match during a loading/unauthenticated state where
 * `accountId` is also `undefined`, showing real financial dispute data for the wrong reason.
 */
export const isDisputedTransactionsOwner = (accountId: string | undefined): boolean =>
  Boolean(DISPUTED_TRANSACTIONS_ACCOUNT_ID) && accountId === DISPUTED_TRANSACTIONS_ACCOUNT_ID;

/**
 * The two access decisions PayerDashboard needs to gate the Disputed Transactions tab, derived
 * from the logged-in account's id in one place -- so the actual decision logic (not just its
 * inputs) can be unit tested independently of rendering PayerDashboard/Apollo.
 */
export const computeDisputedTransactionAccess = (
  accountId: string | undefined,
): { showDisputedTransactions: boolean; showDisputedTransactionRows: boolean } => ({
  showDisputedTransactions: PHILLY_BUS_CARRIER_ACCOUNT_IDS.includes(accountId ?? ""),
  showDisputedTransactionRows: isDisputedTransactionsOwner(accountId),
});
